/**
 * Workspace-controller gate predicates. Pure, no side effects. Centralized
 * so the bootstrap queue, the inbound broadcast handler, and any future
 * caller use identical applicability logic.
 */

import type { WorkspaceStore } from './store'

/**
 * Should an inbound bootstrap (broadcast-triggered) actually run?
 *
 *   - Stale: the controller already diverged; reloading is the only fix.
 *     Applying a bootstrap on top would mix divergent state into server
 *     state and produce a tree that exists in no tab.
 *   - Loading: bootstrap is FIFO-serialized via the bootstrap queue, so
 *     this arm is defensive — by the time `shouldRun` evaluates at
 *     dequeue, the init bootstrap has already drained. If we somehow land
 *     here, defer.
 *   - Loaded: compare the incoming revision against the one we already
 *     have (`state.phase.workspaceRevision` — only exists in Loaded).
 *     Equal or lower revisions mean a no-op fetch.
 *   - persistence queue busy: we have pending outbound writes; their
 *     completion will either broadcast a newer revision (success) or
 *     push us stale (failure), so deferring is safer than clobbering
 *     optimistic state.
 */
export function shouldRunBootstrap(
  state: ReturnType<WorkspaceStore['getState']>,
  incomingRevision: number,
  persistenceQueueBusy: boolean,
): boolean {
  if (persistenceQueueBusy) return false

  switch (state.phase.kind) {
    case 'Stale':
      return false
    case 'Loading':
      // Defensive — should be unreachable: the bootstrap queue's FIFO
      // ordering means init's bootstrap drains before any broadcast task.
      return false
    case 'Loaded':
      // `state.phase.workspaceRevision` is required by the type. No
      // fallback, no "default to 0 because that happens to be correct."
      return incomingRevision > state.phase.workspaceRevision
  }
}
